// src/pages/AdminDevTools.js
//
// Platform-owner-only tooling for creating/maintaining test data locally
// and on staging. This tab only appears at all when the backend confirms
// dev tools are enabled in this environment (see AdminAppSettings.js's
// ping check) -- there is no scenario where this should ever be visible
// in production, since DEV_TOOLS_ENABLED is never set there.
//
// Current tabs: Scenarios & Fixtures (create test scenario, delete test
// organizer fixtures, clear system logs), Edit Participant, and
// Migrations (check-then-run against whatever DB this backend is
// already connected to -- no separate target to configure).

import React, { useState, useEffect } from 'react';
import { clearDevToolsLogs, getDevToolsOrganizers, createDevToolsScenario, deleteDevToolsScenario, updateDevToolsParticipant, getParticipants, getDevToolsMigrationStatus, runDevToolsMigrations } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';

const CURRENT_YEAR = new Date().getFullYear();

const DEV_TOOLS_TABS = [
    { id: 'scenarios',   label: 'Scenarios & Fixtures' },
    { id: 'participant', label: 'Edit Participant' },
    { id: 'migrations',  label: 'Migrations' },
];

function ScenarioBuilder({ showFeedback }) {
    const [organizers, setOrganizers] = useState([]);
    const [orgMode, setOrgMode] = useState('new'); // 'new' | 'existing'
    const [existingOrganizerId, setExistingOrganizerId] = useState('');
    const [organizerName, setOrganizerName] = useState('');
    const [organizerSlug, setOrganizerSlug] = useState('');
    const [planId, setPlanId] = useState('tier1');

    const [tournamentName, setTournamentName] = useState('');
    const [year, setYear] = useState(CURRENT_YEAR);
    const [gender, setGender] = useState('M');

    const [pools, setPools] = useState([{ name: 'Bracket Pool', poolType: 'bracket' }]);

    const [participantCount, setParticipantCount] = useState(0);
    const [participantPrefix, setParticipantPrefix] = useState('Test Participant');
    const [populate, setPopulate] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        getDevToolsOrganizers()
            .then(res => setOrganizers(res.data))
            .catch(() => {});
    }, []);

    const updatePool = (index, field, value) => {
        setPools(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };
    const addPool = () => setPools(prev => [...prev, { name: '', poolType: 'bracket' }]);
    const removePool = (index) => setPools(prev => prev.filter((_, i) => i !== index));

    const canSubmit =
        tournamentName.trim() &&
        pools.length > 0 &&
        pools.every(p => p.name.trim()) &&
        (orgMode === 'existing' ? existingOrganizerId : (organizerName.trim() && organizerSlug.trim()));

    const handleSubmit = async () => {
        setSubmitting(true);
        setResult(null);
        try {
            const payload = {
                tournamentName: tournamentName.trim(),
                year: parseInt(year, 10),
                gender,
                pools: pools.map(p => ({ name: p.name.trim(), poolType: p.poolType })),
                participantCount: parseInt(participantCount, 10) || 0,
                participantPrefix: participantPrefix.trim() || 'Test Participant',
                populate,
                ...(orgMode === 'existing'
                    ? { existingOrganizerId: parseInt(existingOrganizerId, 10) }
                    : { organizerName: organizerName.trim(), organizerSlug: organizerSlug.trim(), planId }),
            };
            const res = await createDevToolsScenario(payload);
            setResult(res.data);
            showFeedback('Scenario created');
            getDevToolsOrganizers().then(r => setOrganizers(r.data)).catch(() => {});
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to create scenario', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-black text-white mb-2">Create test scenario</h3>
            <p className="text-gray-400 text-sm mb-5">
                Creates a fully-formed tournament with named pools under an organizer, going through
                the same real endpoints (and validation) a real organizer would use — not raw database inserts.
            </p>

            {/* Organizer */}
            <div className="mb-5">
                <label className="block text-white font-bold text-sm mb-2">Organizer</label>
                <div className="flex gap-2 mb-3">
                    <button type="button" onClick={() => setOrgMode('new')}
                        className={'px-3 py-1.5 rounded-lg text-sm font-bold ' + (orgMode === 'new' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400')}>
                        New organizer
                    </button>
                    <button type="button" onClick={() => setOrgMode('existing')}
                        className={'px-3 py-1.5 rounded-lg text-sm font-bold ' + (orgMode === 'existing' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400')}>
                        Existing organizer
                    </button>
                </div>

                {orgMode === 'existing' ? (
                    <select value={existingOrganizerId} onChange={e => setExistingOrganizerId(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="">Select an organizer…</option>
                        {organizers.map(o => (
                            <option key={o.id} value={o.id}>{o.display_name} ({o.slug || 'no slug'}) — {o.plan_id}</option>
                        ))}
                    </select>
                ) : (
                    <div className="grid sm:grid-cols-3 gap-3">
                        <input type="text" placeholder="Organizer name" value={organizerName} onChange={e => setOrganizerName(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                        <input type="text" placeholder="slug-for-subdomain" value={organizerSlug} onChange={e => setOrganizerSlug(e.target.value.toLowerCase())}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                        <select value={planId} onChange={e => setPlanId(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                            <option value="eval">Eval</option>
                            <option value="tier1">Starter</option>
                            <option value="tier2">Growth</option>
                            <option value="tier3">Unlimited</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Tournament */}
            <div className="mb-5">
                <label className="block text-white font-bold text-sm mb-2">Tournament</label>
                <div className="grid sm:grid-cols-3 gap-3">
                    <input type="text" placeholder="Tournament name" value={tournamentName} onChange={e => setTournamentName(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    <input type="number" placeholder="Year" value={year} onChange={e => setYear(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    <select value={gender} onChange={e => setGender(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="M">Men's</option>
                        <option value="W">Women's</option>
                    </select>
                </div>
            </div>

            {/* Pools */}
            <div className="mb-5">
                <label className="block text-white font-bold text-sm mb-2">Pools</label>
                <div className="space-y-2">
                    {pools.map((p, i) => (
                        <div key={i} className="flex gap-2">
                            <input type="text" placeholder="Pool name" value={p.name} onChange={e => updatePool(i, 'name', e.target.value)}
                                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                            <select value={p.poolType} onChange={e => updatePool(i, 'poolType', e.target.value)}
                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                                <option value="bracket">Bracket</option>
                                <option value="round_by_round">Round-by-Round</option>
                            </select>
                            {pools.length > 1 && (
                                <button type="button" onClick={() => removePool(i)}
                                    className="text-gray-500 hover:text-red-400 px-2">✕</button>
                            )}
                        </div>
                    ))}
                </div>
                <button type="button" onClick={addPool} className="text-orange-400 hover:text-orange-300 text-sm font-bold mt-2">
                    + Add another pool
                </button>
            </div>

            {/* Participants */}
            <div className="mb-6">
                <label className="block text-white font-bold text-sm mb-2">Additional test participants (optional)</label>
                <p className="text-gray-500 text-xs mb-2">
                    The organizer is always enrolled as a participant in every pool automatically —
                    this adds extra participants on top of that.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-2">
                    <input type="number" min="0" max="100" placeholder="How many?" value={participantCount}
                        onChange={e => setParticipantCount(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    <input type="text" placeholder="Name prefix" value={participantPrefix} onChange={e => setParticipantPrefix(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={populate} onChange={e => setPopulate(e.target.checked)} className="rounded" />
                    Auto-submit Round 1 picks + results for the organizer{parseInt(participantCount, 10) > 0 ? ' and test participants' : ''} (instantly-populated leaderboard)
                </label>
            </div>

            <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm"
            >
                {submitting ? 'Creating…' : 'Create scenario'}
            </button>

            {result && (
                <div className="bg-gray-950 border border-green-800 rounded-lg p-4 mt-4 text-sm text-gray-300 space-y-2">
                    <p className="text-green-400 font-bold mb-1">Scenario created</p>
                    <div>
                        <p className="text-white font-bold">Organizer</p>
                        <p>{result.organizer.name} (id {result.organizer.organizerId})</p>
                        <p className="font-mono text-xs text-gray-400">{result.organizer.email} / {result.password}</p>
                        {result.organizer.slug && (
                            <p className="text-xs text-gray-500">{result.organizer.slug}.heberthoops.com</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Also enrolled as a participant in every pool below — the organizer's login doubles as a player, same as anyone using this account for real.
                        </p>
                    </div>
                    <div>
                        <p className="text-white font-bold">Tournament</p>
                        <p>{result.tournament.name} (id {result.tournament.id})</p>
                        <p>Pools: {result.pools.map(p => p.name).join(', ')}</p>
                    </div>
                    {result.picksMade > 0 && (
                        <p className="text-gray-400">
                            {result.picksMade} Round 1 picks submitted across {result.round1GamesCount} games (organizer{result.participants.length > 0 ? ' + test participants' : ''}).
                        </p>
                    )}
                    {result.participants.length > 0 && (
                        <div>
                            <p className="text-white font-bold">Test participants ({result.participantsCreated})</p>
                            <ul className="mt-1 space-y-0.5">
                                {result.participants.map(p => (
                                    <li key={p.id} className="font-mono text-xs text-gray-400">
                                        {p.name}: {p.email} / {result.password}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ParticipantEditor({ showFeedback }) {
    const [participants, setParticipants] = useState([]);
    const [organizers, setOrganizers] = useState([]);
    const [organizerFilter, setOrganizerFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const loadParticipants = (organizerId) => {
        setLoading(true);
        getParticipants(organizerId || undefined)
            .then(res => setParticipants(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadParticipants();
        getDevToolsOrganizers().then(res => setOrganizers(res.data)).catch(() => {});
    }, []);

    const handleOrganizerFilter = (organizerId) => {
        setOrganizerFilter(organizerId);
        loadParticipants(organizerId);
        // The currently-selected participant may not exist under the new
        // filter (or at all, once "All organizers" is narrowed away from) --
        // clear the form rather than risk it silently pointing at a
        // participant no longer visible in the list.
        setSelectedId('');
        setName('');
        setEmail('');
        setPassword('');
    };

    const selected = participants.find(p => p.id === parseInt(selectedId, 10)) || null;
    const isFixture = !!selected?.email?.endsWith('@devtools.test');

    const handleSelect = (id) => {
        setSelectedId(id);
        const p = participants.find(x => x.id === parseInt(id, 10));
        setName(p ? p.name : '');
        setEmail(p ? (p.email || '') : '');
        setPassword('');
    };

    const dirty = selected && (
        name.trim() !== selected.name ||
        email.trim().toLowerCase() !== (selected.email || '').toLowerCase() ||
        password.length > 0
    );
    const canSave = selected && dirty && name.trim() && email.trim() && (password.length === 0 || password.length >= 8);

    const handleSave = async () => {
        setSaving(true);
        try {
            const fields = {};
            if (name.trim() !== selected.name) fields.name = name.trim();
            if (email.trim().toLowerCase() !== (selected.email || '').toLowerCase()) fields.email = email.trim().toLowerCase();
            if (password.length > 0) fields.password = password;

            const res = await updateDevToolsParticipant(selected.id, fields);
            showFeedback(`Updated "${res.data.name}"`);
            setPassword('');
            loadParticipants(organizerFilter);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to update participant', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-black text-white mb-2">Edit participant</h3>
            <p className="text-gray-400 text-sm mb-5">
                Directly edits name, email, and/or password for any participant. Mainly here for
                email — every other edit path in the app can already touch name and password for
                any participant, but nothing can change email.
            </p>

            {loading ? (
                <p className="text-gray-500 text-sm">Loading…</p>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 gap-3 mb-4">
                        <div>
                            <label className="block text-white font-bold text-sm mb-2">Filter by organizer</label>
                            <select value={organizerFilter} onChange={e => handleOrganizerFilter(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                                <option value="">All organizers</option>
                                {organizers.map(o => (
                                    <option key={o.id} value={o.id}>{o.display_name} ({o.slug || 'no slug'})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-white font-bold text-sm mb-2">Participant</label>
                            <select value={selectedId} onChange={e => handleSelect(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
                                <option value="">Select a participant…</option>
                                {participants.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.email || 'no email'})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selected && (
                        <>
                            {!isFixture && (
                                <div className="bg-yellow-950/40 border border-yellow-700 rounded-lg p-3 text-xs text-yellow-300 mb-4">
                                    Not a devtools fixture (@devtools.test) — this is a real participant account.
                                </div>
                            )}
                            <div className="grid sm:grid-cols-3 gap-3 mb-4">
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">Name</label>
                                    <input type="text" value={name} onChange={e => setName(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">Email</label>
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs mb-1">New password (optional)</label>
                                    <input type="text" value={password} onChange={e => setPassword(e.target.value)}
                                        placeholder="Leave blank to keep current"
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                                </div>
                            </div>
                            <button
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm"
                            >
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function MigrationRunner({ showFeedback }) {
    const [checking, setChecking] = useState(false);
    const [status, setStatus] = useState(null); // { target, pending, log } | null
    const [running, setRunning] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [showSql, setShowSql] = useState(false);

    const handleCheck = async () => {
        setChecking(true);
        setStatus(null);
        try {
            const res = await getDevToolsMigrationStatus();
            setStatus(res.data);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to check migration status', 'error');
        } finally {
            setChecking(false);
        }
    };

    const handleRun = async () => {
        setRunning(true);
        try {
            const res = await runDevToolsMigrations();
            const count = res.data.applied.length;
            showFeedback(`Applied ${count} migration${count === 1 ? '' : 's'}: ${res.data.applied.join(', ')}`);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Migration run failed', 'error');
        } finally {
            setRunning(false);
            setDialogOpen(false);
            setStatus(null); // force a fresh Check before showing pending state again
            setShowSql(false);
        }
    };

    return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-black text-white mb-2">Run migrations</h3>
            <p className="text-gray-400 text-sm mb-5">
                Runs pending migrations against whatever database this backend is already
                connected to -- no separate target to configure. Only ever applies (never rolls
                back); use the CLI for down migrations. Always check first, which is read-only,
                before running.
            </p>

            <button
                onClick={handleCheck}
                disabled={checking}
                className="bg-gray-800 hover:bg-gray-700 disabled:opacity-50 border border-gray-600 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm mb-4"
            >
                {checking ? 'Checking…' : 'Check for pending migrations'}
            </button>

            {status && (
                <div className="border border-gray-700 rounded-lg p-4 bg-gray-950">
                    <p className="text-gray-400 text-xs mb-3">
                        Target: <span className="text-gray-200 font-mono">{status.target}</span>
                    </p>

                    {status.pending.length === 0 ? (
                        <p className="text-green-400 text-sm font-bold">Up to date -- no pending migrations.</p>
                    ) : (
                        <>
                            <p className="text-white text-sm font-bold mb-2">
                                {status.pending.length} pending migration{status.pending.length === 1 ? '' : 's'}:
                            </p>
                            <ul className="text-gray-300 text-sm font-mono mb-3 space-y-1">
                                {status.pending.map(name => <li key={name}>- {name}</li>)}
                            </ul>

                            <button
                                onClick={() => setShowSql(s => !s)}
                                className="text-orange-400 hover:text-orange-300 text-xs font-bold mb-3"
                            >
                                {showSql ? 'Hide SQL' : 'Show SQL'}
                            </button>

                            {showSql && (
                                <pre className="bg-black rounded-lg p-3 text-gray-400 text-xs overflow-x-auto mb-4 whitespace-pre-wrap">
                                    {status.log.map(e => e.msg).join('\n')}
                                </pre>
                            )}

                            <button
                                onClick={() => setDialogOpen(true)}
                                disabled={running}
                                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm"
                            >
                                Run {status.pending.length} pending migration{status.pending.length === 1 ? '' : 's'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {dialogOpen && status && (
                <ConfirmDialog
                    message={`This runs ${status.pending.length} pending migration(s) against ${status.target}: ${status.pending.join(', ')}. This directly alters the database schema and cannot be undone by this tool.`}
                    requireTyped
                    onConfirm={handleRun}
                    onCancel={() => setDialogOpen(false)}
                />
            )}
        </div>
    );
}

function DeleteFixtures({ showFeedback }) {
    const [organizers, setOrganizers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingDelete, setPendingDelete] = useState(null); // organizer object, or null
    const [deleting, setDeleting] = useState(false);

    const loadOrganizers = () => {
        setLoading(true);
        getDevToolsOrganizers()
            .then(res => setOrganizers(res.data.filter(o => o.is_devtools_fixture)))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadOrganizers(); }, []);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await deleteDevToolsScenario(pendingDelete.id);
            const tCount = res.data.tournamentsDeleted.length;
            const pCount = res.data.participantsDeleted.length;
            showFeedback(
                `Deleted "${res.data.organizerDeleted.name}" — ${tCount} tournament${tCount === 1 ? '' : 's'}, ${pCount} test participant${pCount === 1 ? '' : 's'}`
            );
            loadOrganizers();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to delete organizer fixture', 'error');
        } finally {
            setDeleting(false);
            setPendingDelete(null);
        }
    };

    return (
        <div className="bg-gray-900 border border-red-900 rounded-xl p-6">
            <h3 className="text-lg font-black text-red-400 mb-2">Delete test organizer fixtures</h3>
            <p className="text-gray-400 text-sm mb-4">
                Deletes an organizer created by the scenario builder above, along with all of its
                tournaments, pools, games, and picks, plus any test participants left with no other
                enrollments. Only ever touches organizers whose login was created by this tool —
                refuses to run against a real organizer, even by mistake.
            </p>

            {loading ? (
                <p className="text-gray-500 text-sm">Loading…</p>
            ) : organizers.length === 0 ? (
                <p className="text-gray-500 text-sm">No test organizer fixtures found.</p>
            ) : (
                <div className="space-y-2">
                    {organizers.map(o => (
                        <div key={o.id} className="flex items-center justify-between bg-gray-950 border border-gray-800 rounded-lg px-4 py-2">
                            <div>
                                <p className="text-white text-sm font-bold">{o.display_name}</p>
                                <p className="text-gray-500 text-xs">{o.slug || 'no slug'} — {o.plan_id}</p>
                            </div>
                            <button
                                onClick={() => setPendingDelete(o)}
                                disabled={deleting}
                                className="text-red-400 hover:text-red-300 disabled:opacity-50 text-sm font-bold"
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {pendingDelete && (
                <ConfirmDialog
                    message={`This permanently deletes "${pendingDelete.display_name}" and every tournament, pool, game, pick, and test participant under it. This cannot be undone.`}
                    requireTyped
                    onConfirm={handleDelete}
                    onCancel={() => setPendingDelete(null)}
                />
            )}
        </div>
    );
}

function AdminDevTools({ showFeedback }) {
    const [tab, setTab] = useState('scenarios');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [clearing, setClearing] = useState(false);

    const handleClearLogs = async () => {
        setClearing(true);
        try {
            const res = await clearDevToolsLogs();
            showFeedback(`Cleared ${res.data.cleared} log ${res.data.cleared === 1 ? 'entry' : 'entries'}`);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to clear system logs', 'error');
        } finally {
            setClearing(false);
            setDialogOpen(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="bg-yellow-950/40 border border-yellow-700 rounded-xl p-4 text-sm text-yellow-300">
                These tools are only visible because dev tools are enabled in this environment
                (local or staging). They never appear in production.
            </div>

            {/* Sub-nav -- same styling as AdminAppSettings.js's SUB_TABS bar,
                kept local here since these tabs only ever apply within
                Dev Tools. */}
            <div className="flex gap-1 border-b border-gray-800">
                {DEV_TOOLS_TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ' +
                            (tab === t.id
                                ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                : 'text-gray-500 hover:text-gray-300')}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'scenarios' && (
                <div className="space-y-4">
                    <ScenarioBuilder showFeedback={showFeedback} />

                    <DeleteFixtures showFeedback={showFeedback} />

                    <div className="bg-gray-900 border border-red-900 rounded-xl p-6">
                        <h3 className="text-lg font-black text-red-400 mb-2">Clear system logs</h3>
                        <p className="text-gray-400 text-sm mb-4">
                            Permanently deletes every row from the audit log (System Logs tab) --
                            across every organizer and tournament, not just the one currently selected.
                            This cannot be undone.
                        </p>
                        <button
                            onClick={() => setDialogOpen(true)}
                            disabled={clearing}
                            className="bg-red-900 hover:bg-red-800 disabled:opacity-50 border border-red-700 text-red-300 font-bold px-6 py-2 rounded-lg transition-colors text-sm"
                        >
                            Clear system logs
                        </button>
                    </div>
                </div>
            )}

            {tab === 'participant' && (
                <ParticipantEditor showFeedback={showFeedback} />
            )}

            {tab === 'migrations' && (
                <MigrationRunner showFeedback={showFeedback} />
            )}

            {dialogOpen && (
                <ConfirmDialog
                    message="This permanently deletes every entry in the audit log, across every organizer and tournament on this environment. This cannot be undone."
                    requireTyped
                    onConfirm={handleClearLogs}
                    onCancel={() => setDialogOpen(false)}
                />
            )}
        </div>
    );
}

export default AdminDevTools;